/**
 * The words for a bill's stored status and source, one owner. The Accounts list, its CSV and the
 * bill's own page all print a status; the bill page used to print the stored value
 * ("partially_paid", "vendor_invoice") as it came.
 */

/** A bill's status as a person reads it. Unknown values are shown as stored rather than hidden. */
export function billStatusLabel(status: string | null | undefined): string {
  const s = String(status || '').toLowerCase();
  if (s === 'unpaid') return 'Unpaid';
  if (s === 'partially_paid') return 'Partial';
  if (s === 'paid') return 'Paid';
  if (s === 'overdue') return 'Overdue';
  if (s === 'on_hold') return 'On Hold';
  if (s === 'disputed') return 'Disputed';
  return String(status || '--');
}

/** Where a bill came from. */
export function billSourceLabel(kind: string | null | undefined): string {
  const k = String(kind || '').toLowerCase();
  if (k === 'vendor_invoice') return 'Vendor invoice';
  if (k === 'manual') return 'Entered by hand';
  return kind ? String(kind).replace(/_/g, ' ') : '--';
}

/**
 * The three words a bill's status reads as on the everyday list (2026-09-22, at the
 * owner's word): Paid, Partially Paid, or Unpaid. Read off the money, not the stored
 * status word, so a bill someone has put on hold or disputed still reads as what is
 * actually owed rather than vanishing into a fourth word nobody asked for. "Overdue" is
 * not a status of its own here either — a bill past its due date is still Unpaid/Partially
 * Paid, just in red (see `overdueBillRow` below). On Hold and Disputed are real states
 * underneath (they still stop a payment run from picking the bill up) and still show in
 * full on the bill's own page (`billStatusLabel` above) — they only stop being their own
 * word on the list everyone reads day to day.
 */
export function simpleBillStatusLabel(row: {
  paidAmount?: number | null;
  outstandingAmount?: number | null;
}): 'Paid' | 'Partially Paid' | 'Unpaid' {
  // Missing is not zero (CLAUDE.md invariant 8): an outstanding amount the row never
  // carried must never read as "nothing is owed" -- Unpaid is the safe default.
  const outstandingRaw = row.outstandingAmount;
  const outstanding = typeof outstandingRaw === 'number' && Number.isFinite(outstandingRaw) ? outstandingRaw : null;
  const paidRaw = row.paidAmount;
  const paid = typeof paidRaw === 'number' && Number.isFinite(paidRaw) ? paidRaw : 0;
  if (outstanding === null) return 'Unpaid';
  if (outstanding <= 0.005) return 'Paid';
  if (paid > 0.005) return 'Partially Paid';
  return 'Unpaid';
}

/** Whether a bill is on hold or disputed — an exception, flagged apart from the plain status word. */
export function isExceptionStatus(status: string | null | undefined): boolean {
  const s = String(status || '').toLowerCase();
  return s === 'on_hold' || s === 'disputed';
}

/** The word for that exception, e.g. beside a small warning flag. Blank for an ordinary bill. */
export function exceptionStatusLabel(status: string | null | undefined): string {
  const s = String(status || '').toLowerCase();
  if (s === 'on_hold') return 'On hold';
  if (s === 'disputed') return 'Disputed';
  return '';
}
